import { Injectable, OnDestroy } from '@angular/core';

import { Observable, Subject } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { SubscriptionApiProviderService } from '@core/api';
import {
  TSubscription,
  TSubscriptionRequest,
} from '../../data-providers/api-providers/subscription-api-provider/subscription-requests.model';
import { ResponseErrorService } from '../errors/response-error.service';
import { ESubscriptionType } from './user-settings/subscription-types.enum';

export type TSubscriptions = {
  data: TSubscription[];
};

export type TSubscriptionsById = {
  data: {
    [subscriptionId: string]: TSubscription;
  };
};

@Injectable({
  providedIn: 'root',
})
export class SubscriptionsService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();

  private subscriptions: TSubscriptions;
  private subscriptionsById: TSubscriptionsById;

  constructor(
    private subscriptionApiProviderService: SubscriptionApiProviderService,
    private responseErrorService: ResponseErrorService,
  ) {
    this.subscriptions = { data: [] };
    this.subscriptionsById = { data: {} };
  }

  ngOnDestroy() {
    this.destroy$.next();
  }

  getSubscriptions(): TSubscriptions {
    return this.subscriptions;
  }

  setSubscriptions(data: TSubscription[]): TSubscriptions {
    this.subscriptions.data = data;

    return this.getSubscriptions();
  }

  getSubscriptionsById(): TSubscriptionsById {
    return this.subscriptionsById;
  }

  createSubscriptionsById(): void {
    this.subscriptions.data.forEach((subscription) => {
      this.subscriptionsById.data[subscription.workspaceId] = subscription;
    });
  }

  fetchSubscriptions(): Observable<TSubscription[]> {
    return this.subscriptionApiProviderService.fetchSubscriptions().pipe(
      tap((response) => {
        this.subscriptions.data = response;
        this.createSubscriptionsById();
      }),
      catchError((error) =>
        this.responseErrorService.handleRequestError(error, 'Failed to fetch subscriptions'),
      ),
    );
  }

  updateSubscriptions(newSubscriptions: TSubscription[]): void {
    newSubscriptions.forEach((subscription) => {
      const existingSubscription = this.subscriptions.data.find(
        (oldSubscription) => oldSubscription.workspaceId === subscription.workspaceId,
      );

      if (existingSubscription) {
        existingSubscription.subscriptionType = subscription.subscriptionType;
        existingSubscription.id = subscription.id;
      } else {
        this.subscriptions.data.push(subscription);
      }
    });

    this.createSubscriptionsById();
  }

  addSubscription(subscription: TSubscriptionRequest): Observable<TSubscription> {
    return this.subscriptionApiProviderService
      .addSubscriptions(subscription)
      .pipe(
        catchError((error) =>
          this.responseErrorService.handleRequestError(error, 'Failed to add subscription'),
        ),
      );
  }

  updateSubscription(
    subscriptionId: string,
    subscription: TSubscriptionRequest,
  ): Observable<TSubscription> {
    return this.subscriptionApiProviderService
      .updateSubscription(subscriptionId, subscription)
      .pipe(
        catchError((error) =>
          this.responseErrorService.handleRequestError(error, 'Failed to update subscription'),
        ),
      );
  }

  bulkUpdateSubscriptions(subscriptionType: ESubscriptionType): Observable<TSubscription[]> {
    return this.subscriptionApiProviderService
      .bulkUpdateSubscriptions(subscriptionType)
      .pipe(
        catchError((error) =>
          this.responseErrorService.handleRequestError(
            error,
            'Failed to bulk update subscriptions',
          ),
        ),
      );
  }

  deleteSubscription(subscriptionId: string): Observable<null> {
    return this.subscriptionApiProviderService
      .deleteSubscription(subscriptionId)
      .pipe(
        catchError((error) =>
          this.responseErrorService.handleRequestError(error, 'Failed to delete subscription'),
        ),
      );
  }
}
